/**
 * W1-11b (app-shell-ui): command state for the shell's surfaces (palette, menus, hint bar, cheat sheet, the
 * UiBridge behind buttons). Works with any `Registry` (the contract): the app registry's own `availability` and
 * `whenContext` are used when present, otherwise the same rules are applied here (command `when`, then `enabled`).
 */

import type { Json, MessageRef } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import { detectOs } from '../registry/chords';
import type {
  AppServices,
  CommandContext,
  CommandDef,
  CommandId,
  CommandSource,
  KeyBinding,
  MenuId,
  Registry,
  WhenContext,
} from '../registry/types';
import { buildWhenContext, compileWhen, type CompiledWhen } from '../registry/when';
import { m } from './shell.messages';

/** Optional members of the app registry (registryCore.ts AppRegistry), feature-detected. */
interface RegistryExtras {
  availability?(id: CommandId): true | MessageRef | null;
  whenContext?(): WhenContext;
}

function extras(r: Registry): RegistryExtras {
  return r as Registry & RegistryExtras;
}

/** The current editor context: the given provider, else the app registry's (router-backed), else built here. */
export function currentWhen(services: AppServices, provider?: (() => WhenContext) | null): WhenContext {
  if (provider !== undefined && provider !== null) return provider();
  try {
    const viaRegistry = extras(services.registry).whenContext?.();
    if (viaRegistry !== undefined) return viaRegistry;
  } catch {
    // fall through to the local context
  }
  return buildWhenContext({
    services,
    os: detectOs(),
    route: typeof window === 'undefined' ? '' : window.location.hash,
  });
}

/** A CommandContext over the services (live getters kept through the prototype chain). */
export function commandContext(
  services: AppServices,
  when: WhenContext,
  source: CommandSource,
  event?: KeyboardEvent | PointerEvent | WheelEvent,
): CommandContext {
  const ctx = Object.create(services) as Record<string, unknown>;
  Object.defineProperty(ctx, 'when', { value: when, enumerable: true });
  Object.defineProperty(ctx, 'source', { value: source, enumerable: true });
  if (event !== undefined) Object.defineProperty(ctx, 'event', { value: event, enumerable: true });
  return ctx as unknown as CommandContext;
}

const whenCache = new Map<string, CompiledWhen | { readonly error: string }>();
let whenCacheAtoms: ReadonlySet<string> | null = null;

function compiled(expr: string, atoms: ReadonlySet<string>): CompiledWhen | { readonly error: string } {
  if (whenCacheAtoms !== atoms) {
    whenCache.clear();
    whenCacheAtoms = atoms;
  }
  let c = whenCache.get(expr);
  if (c === undefined) {
    c = compileWhen(expr, atoms);
    whenCache.set(expr, c);
  }
  return c;
}

/** Does a condition hold in this context? Invalid conditions never hold. */
export function whenHolds(expr: string | undefined, ctx: WhenContext, atoms: ReadonlySet<string>): boolean {
  if (expr === undefined || expr.trim() === '') return true;
  const c = compiled(expr, atoms);
  if ('error' in c) return false;
  try {
    return c.eval(ctx);
  } catch {
    return false;
  }
}

/** Command state: runnable, disabled with a reason, or not applicable here (condition false / unknown). */
export type CommandState =
  | { readonly kind: 'enabled' }
  | { readonly kind: 'disabled'; readonly reason: MessageRef }
  | { readonly kind: 'hidden'; readonly reason: MessageRef };

const ENABLED: CommandState = { kind: 'enabled' };

export function ref(key: MessageKey, params?: Readonly<Record<string, string | number>>): MessageRef {
  return params === undefined ? { key } : { key, params };
}

/** State of a command now (palette, menus, hint bar). */
export function commandState(
  services: AppServices,
  id: CommandId,
  when?: WhenContext,
  source: CommandSource = 'api',
): CommandState {
  const registry = services.registry;
  const def = registry.command(id);
  if (def === undefined) return { kind: 'hidden', reason: ref(m.reasonUnknownCommand) };
  if (when === undefined) {
    const a = extras(registry).availability?.(id);
    if (a === true) return ENABLED;
    if (a === null) return { kind: 'hidden', reason: ref(m.reasonNotHere) };
    if (a !== undefined) return { kind: 'disabled', reason: a };
  }
  const ctx = when ?? currentWhen(services);
  if (!whenHolds(def.when, ctx, registry.whenAtomNames())) return { kind: 'hidden', reason: ref(m.reasonNotHere) };
  try {
    const r = def.enabled?.(commandContext(services, ctx, source)) ?? true;
    return r === true ? ENABLED : { kind: 'disabled', reason: r };
  } catch {
    return { kind: 'disabled', reason: ref(m.reasonNotHere) };
  }
}

/** UiBridge `enabled` semantics: true, or the reason (a command not applicable here is disabled with a reason). */
export function commandEnabled(services: AppServices, id: CommandId, when?: WhenContext): true | MessageRef {
  const s = commandState(services, id, when);
  return s.kind === 'enabled' ? true : s.reason;
}

/** `checked()` of a toggle command, or undefined when it is not a toggle (or fails). */
export function commandChecked(services: AppServices, def: CommandDef, when: WhenContext): boolean | undefined {
  if (def.checked === undefined) return undefined;
  try {
    return def.checked(commandContext(services, when, 'api'));
  } catch {
    return undefined;
  }
}

/** Stable text of JSON args (binding <-> args comparisons). */
export function argsKey(args: Json | undefined): string {
  if (args === undefined) return '';
  const norm = (v: Json): Json => {
    if (Array.isArray(v)) return v.map((x: Json) => norm(x));
    if (v !== null && typeof v === 'object') {
      const o = v as { readonly [k: string]: Json };
      return Object.fromEntries(
        Object.keys(o)
          .sort()
          .map((k) => [k, norm(o[k] as Json)]),
      );
    }
    return v;
  };
  return JSON.stringify(norm(args));
}

/** Chords bound to a command with exactly these args (the workspace tabs: F2 / F3 / F4 of one command). */
export function chordsFor(bindings: readonly KeyBinding[], command: CommandId, args?: Json): string[] {
  const key = argsKey(args);
  return bindings
    .filter((b) => b.command === command && b.remove !== true && argsKey(b.args) === key)
    .map((b) => b.chord);
}

/**
 * Chords that run `command` with `value` as its argument (the palette's argument step): bindings whose args equal
 * the value, or whose args object holds it as its only field (`{ div: 4 }` for 4, `{ workspace: 'chart' }` for
 * 'chart').
 */
export function chordsForArg(bindings: readonly KeyBinding[], command: CommandId, value: Json): string[] {
  const key = argsKey(value);
  return bindings
    .filter((b) => {
      if (b.command !== command || b.remove === true || b.args === undefined) return false;
      if (argsKey(b.args) === key) return true;
      const a = b.args;
      if (a === null || typeof a !== 'object' || Array.isArray(a)) return false;
      const fields = Object.values(a as { readonly [k: string]: Json });
      return fields.length === 1 && fields[0] !== undefined && argsKey(fields[0]) === key;
    })
    .map((b) => b.chord);
}

/** A registry menu as ordered groups of command ids (empty groups dropped). */
export function menuGroups(registry: Registry, id: MenuId): ReadonlyArray<readonly CommandDef[]> {
  return registry
    .menu(id)
    .map((g) => g.commands)
    .filter((g) => g.length > 0);
}
